import React, { useState, useCallback, ReactElement } from 'react';
import * as css from './OilInformation.module.scss';
import 'src/css/themes.scss';
import { IconSeeMore } from 'src/content/icons';
import { ConnectedVehicleRec } from 'src/models';
import { Dialog, ScrollBar } from '@framework-controls/core';
import { strings } from 'src/localization';

export interface IProps {
    connectedVehicle: ConnectedVehicleRec;
}

const OilInformation = (props: IProps) => {
    const [infoOpen, setInfoOpen] = useState(false);
    const toggleInfo = useCallback(() => {
        setInfoOpen(!infoOpen);
    }, [infoOpen]);

    let oilIcon: ReactElement | null = null;
    if (props.connectedVehicle.oilIcon) {
        oilIcon =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.Oil}
                </div>
                <iframe className={css.iframe} title='OilIcon' src={props.connectedVehicle.oilIcon}></iframe>
            </span>;
    }

    let oilLife: ReactElement | null = null;
    if (props.connectedVehicle.oilLifeValue) {
        oilLife =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.OilLife}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.oilLifeValue}
                </div>
            </span>;
    }

    let oilStatus: ReactElement | null = null;
    if (props.connectedVehicle.oilStatus) {
        let statusLit = strings.DataAsOf;
        if (props.connectedVehicle.oilCheckStatus) {
            statusLit = props.connectedVehicle.oilCheckStatus + ' ' + strings.AsOf;
        }
        let oilTstamp: ReactElement | null = null;
        if (props.connectedVehicle.oilTstamp) {
            oilTstamp =
                <p className={css.statusLit}>{statusLit + ' ' + props.connectedVehicle.oilTstamp}</p>;
        }
        oilStatus =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.OilStatus}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.oilStatus}
                </div>
                {oilTstamp}
            </span>;
    }

    let oilAmount: ReactElement | null = null;
    if (props.connectedVehicle.oilQtyStatus) {
        oilAmount =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.OilAmount}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.oilQtyStatus}
                </div>
            </span>;
    }

    let oilDesc: ReactElement | null = null;
    if (props.connectedVehicle.oilDesc) {
        oilDesc =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.OilDescription}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.oilDesc}
                </div>
            </span>;
    }

    let remainingDistance: ReactElement | null = null;
    if (props.connectedVehicle.oilRemainDistance) {
        remainingDistance =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.RemainingDistance}
                </div>
                <div className={css.infoData}>
                    {props.connectedVehicle.oilRemainDistance}
                </div>
            </span>;
    }

    let replaceOil: ReactElement | null = null;
    if (props.connectedVehicle.oilReplaceIcon || props.connectedVehicle.oilReplaceStatus || props.connectedVehicle.oilReplaceDesc ||
        props.connectedVehicle.oilReplaceTstamp) {
        let replaceOilIcon: ReactElement | null = null;
        if (props.connectedVehicle.oilReplaceIcon) {
            replaceOilIcon =
                <iframe className={css.iframe} title='ReplaceOilIcon' src={props.connectedVehicle.oilReplaceIcon}></iframe>;
        }
        let replaceOilStatus: ReactElement | null = null;
        if (props.connectedVehicle.oilReplaceStatus) {
            replaceOilStatus =
                <div className={css.infoData}>
                    {props.connectedVehicle.oilReplaceStatus}
                </div>;
        }
        let replaceOilDesc: ReactElement | null = null;
        if (props.connectedVehicle.oilReplaceDesc) {
            replaceOilDesc =
                <div className={css.infoData}>
                    {props.connectedVehicle.oilReplaceDesc}
                </div>;
        }
        let replaceOilTstamp: ReactElement | null = null;
        if (props.connectedVehicle.oilReplaceTstamp) {
            replaceOilTstamp =
                <p className={css.statusLit}>{strings.DataAsOf + ' ' + props.connectedVehicle.oilReplaceTstamp}</p>;
        }
        replaceOil =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ReplaceOil}
                </div>
                {replaceOilIcon}
                {replaceOilStatus}
                {replaceOilDesc}
                {replaceOilTstamp}
            </span>;
    }

    let zero: ReactElement | null = null;
    if (props.connectedVehicle.oilZeroDays || props.connectedVehicle.oilZeroMonths || props.connectedVehicle.oilZeroYears) {
        let zeroData: string | null = null;
        if (props.connectedVehicle.oilZeroDays) {
            zeroData = props.connectedVehicle.oilZeroDays + '/';
        } else {
            zeroData = '/';
        }
        if (props.connectedVehicle.oilZeroMonths) {
            zeroData = zeroData + props.connectedVehicle.oilZeroMonths + '/';
        } else {
            zeroData = zeroData + '/';
        }
        if (props.connectedVehicle.oilZeroYears) {
            zeroData = zeroData + props.connectedVehicle.oilZeroYears;
        } else {
            zeroData = zeroData + '/';
        }
        zero =
            <span className={css.infoBlock}>
                <div className={css.infoLit}>
                    {strings.ZeroDaysMonthsYears}
                </div>
                <div className={css.infoData}>
                    {zeroData}
                </div>
            </span>;
    }

    return (
        <div className={css.block}>
            <div className={css.icon}>
                <IconSeeMore onClick={() => toggleInfo()} />
            </div>
            <Dialog
                cssClasses={{
                    content: css.content,
                    paper: css.paper,
                    header: css.LightBoxHeader,
                }}
                isOpen={infoOpen}
                anchorOrigin={{
                    vertical: 'top',
                }}
                header={strings.OilInformation.toUpperCase()}
                hideSave={true}
                hideCancel={true}
                disableUpperCase={true}
                onCancel={() => toggleInfo()}
                onBackDropClick={() => toggleInfo()}
                width={'full'}
            >
                <ScrollBar scrollVertical>
                    <div className={css.infoRow}>
                        {oilIcon}
                        {oilLife}
                        {oilStatus}
                        {oilAmount}
                        {oilDesc}
                        {remainingDistance}
                        {replaceOil}
                        {zero}
                    </div>
                </ScrollBar>
            </Dialog>
        </div>
    );
};

export default OilInformation;